import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isInitialized, login, error, isLoggingIn } = useAuth();
  const { inProgress } = useMsal();
  const location = useLocation();

  // Save the current location for redirect after login
  useEffect(() => {
    if (!isAuthenticated && isInitialized && inProgress === InteractionStatus.None) {
      const currentPath = location.pathname + location.search;
      if (currentPath !== '/' && currentPath !== '/login') {
        sessionStorage.setItem('redirectUrl', currentPath);
      }
    }
  }, [isAuthenticated, isInitialized, inProgress, location]);

  // Show loading while initializing or during MSAL interaction
  if (!isInitialized || inProgress !== InteractionStatus.None) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-8 bg-white rounded-lg shadow-sm text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-medium text-gray-900">Authenticating...</h2>
          <p className="text-sm text-gray-500 mt-2">
            {inProgress === InteractionStatus.Login ? 'Signing you in...' : 'Checking authentication status'}
          </p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome</h2>
            <p className="text-gray-600">Sign in to access your account</p>
          </div>
          
          <div className="bg-white py-8 px-6 shadow-sm rounded-lg">
            {error && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    {error.includes('Multiple Microsoft accounts') || error.includes('cache') ? (
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${error.includes('Multiple Microsoft accounts') || error.includes('cache') ? 'text-blue-700' : 'text-red-700'}`}>
                      {error}
                    </p>
                    {error.includes('cache') && (
                      <button
                        onClick={() => {
                          // Clear all localStorage
                          localStorage.clear();
                          sessionStorage.clear();
                          window.location.reload();
                        }}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-500 underline"
                      >
                        Clear cache and reload
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={login}
              disabled={isLoggingIn}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoggingIn ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
                  </svg>
                  Sign in with Microsoft
                </div>
              )}
            </button>
            
            <p className="mt-4 text-center text-xs text-gray-500">
              You'll be redirected to Microsoft to choose your account and sign in
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated, show the protected content
  return <>{children}</>;
};

export default ProtectedRoute;